import { useEffect, useState } from "react";
import { FIELD_CATALOG, CUSTOM_FIELD_TYPES, makeCustomKey } from "../../lib/formFields";
import api from "../../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { inputCls, labelCls, btnPrimary, btnSecondary } from "./common";
import { Plus, Trash2, Sparkles, ChevronUp, ChevronDown, Pencil, Settings2, X } from "lucide-react";
import { toast } from "sonner";

const BLANK = { label: "", type: "text", options: [], placeholder: "", help_text: "", checkbox_label: "", required: false };

export default function FormFieldsBuilder({ value, onChange }) {
  const fields = value || [];
  const [dlg, setDlg] = useState(false);
  const [editIdx, setEditIdx] = useState(null);
  const [draft, setDraft] = useState(BLANK);
  const [optsIdx, setOptsIdx] = useState(null); // index of built-in select field editing options
  const [library, setLibrary] = useState([]); // reusable saved custom fields

  useEffect(() => {
    api.get("/field-library").then((r) => setLibrary(r.data || [])).catch(() => {});
  }, []);

  const usedKeys = new Set(fields.map((f) => f.key));
  const available = FIELD_CATALOG.filter((f) => !usedKeys.has(f.key));
  const libraryAvailable = library.filter((f) => !usedKeys.has(f.key));

  const addBuiltIn = (key) => {
    if (!key) return;
    const def = FIELD_CATALOG.find((f) => f.key === key);
    if (!def) return;
    onChange([...fields, { ...def, required: false }]); // default OFF per requirement
  };

  const addFromLibrary = (key) => {
    const f = library.find((x) => x.key === key);
    if (!f) return;
    onChange([...fields, {
      key: f.key, label: f.label, type: f.type, group: f.group || "Additional Information",
      options: f.options || [], placeholder: f.placeholder || "", help_text: f.help_text || "",
      checkbox_label: f.checkbox_label || "", custom: true, required: false,
    }]);
    toast.success(`Added saved field "${f.label}"`);
  };

  const onPickField = (val) => {
    if (!val) return;
    if (val.startsWith("lib::")) addFromLibrary(val.slice(5));
    else addBuiltIn(val);
  };

  const openCustom = () => { setDraft(BLANK); setEditIdx(null); setDlg(true); };
  const openEdit = (i) => {
    const f = fields[i];
    setDraft({ label: f.label, type: f.type, options: f.options || [], placeholder: f.placeholder || "", help_text: f.help_text || "", checkbox_label: f.checkbox_label || "", required: !!f.required, key: f.key, custom: f.custom });
    setEditIdx(i); setDlg(true);
  };

  const saveDraft = async () => {
    if (!draft.label.trim()) { toast.error("Field label is required"); return; }
    if (draft.type === "select" && draft.options.filter((o) => o.trim()).length === 0) { toast.error("Add at least one dropdown option"); return; }
    const field = {
      key: editIdx !== null ? draft.key : makeCustomKey(draft.label),
      label: draft.label.trim(), type: draft.type, group: "Additional Information",
      required: draft.required, custom: true,
      placeholder: draft.placeholder, help_text: draft.help_text,
    };
    if (draft.type === "select") field.options = draft.options.map((o) => o.trim()).filter(Boolean);
    if (draft.type === "checkbox") field.checkbox_label = draft.checkbox_label || draft.label;
    if (editIdx !== null) onChange(fields.map((f, idx) => (idx === editIdx ? { ...f, ...field } : f)));
    else onChange([...fields, field]);
    setDlg(false);
    // Persist to the reusable field library so this custom field can be
    // picked directly next time, on any form.
    try {
      const { data } = await api.post("/field-library", field);
      setLibrary((prev) => [...prev.filter((x) => x.key !== data.key && x.label.toLowerCase() !== data.label.toLowerCase()), data]);
    } catch (e) { /* non-fatal: backend also auto-saves on page save */ }
  };

  const update = (i, patch) => onChange(fields.map((f, idx) => (idx === i ? { ...f, ...patch } : f)));
  const remove = (i) => { if (window.confirm(`Delete field "${fields[i].label}" from this form?`)) onChange(fields.filter((_, idx) => idx !== i)); };
  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= fields.length) return;
    const copy = [...fields];
    [copy[i], copy[j]] = [copy[j], copy[i]];
    onChange(copy);
  };

  return (
    <div className="space-y-3">
      <p className="text-xs text-slate-500">Tick <span className="font-semibold">Required</span> to show a field on the customer form and make it compulsory. Un-ticked fields are hidden from customers.</p>
      <div className="space-y-2">
        {fields.map((f, i) => (
          <div key={f.key} className="flex items-center gap-3 bg-slate-50 border border-slate-200 rounded-xl px-3 py-2.5" data-testid={`form-field-${f.key}`}>
            <div className="flex flex-col text-slate-300">
              <button type="button" onClick={() => move(i, -1)} className="hover:text-slate-600"><ChevronUp className="w-3.5 h-3.5" /></button>
              <button type="button" onClick={() => move(i, 1)} className="hover:text-slate-600"><ChevronDown className="w-3.5 h-3.5" /></button>
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="font-medium text-slate-800 text-sm truncate">{f.label}</span>
                {f.custom && <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-violet-600 bg-violet-50 border border-violet-200 px-1.5 py-0.5 rounded-full"><Sparkles className="w-2.5 h-2.5" /> Custom</span>}
              </div>
              <div className="text-[11px] text-slate-400 font-mono">{f.type === "select" ? "dropdown" : f.type}{f.options?.length ? ` · ${f.options.join(", ")}` : ""}</div>
            </div>
            <label className="flex items-center gap-1.5 text-xs text-slate-500 cursor-pointer select-none">
              <input type="checkbox" checked={!!f.required} onChange={(e) => update(i, { required: e.target.checked })} className="rounded border-slate-300 text-blue-600" data-testid={`required-${f.key}`} />
              Required
            </label>
            {f.type === "select" && !f.custom && (
              <button type="button" onClick={() => setOptsIdx(i)} className="w-8 h-8 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-700 flex items-center justify-center" title="Configure options" data-testid={`options-${f.key}`}><Settings2 className="w-4 h-4" /></button>
            )}
            {f.custom && (
              <button type="button" onClick={() => openEdit(i)} className="w-8 h-8 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-blue-600 flex items-center justify-center" title="Edit" data-testid={`edit-field-${f.key}`}><Pencil className="w-4 h-4" /></button>
            )}
            <button type="button" onClick={() => remove(i)} className="w-8 h-8 rounded-lg hover:bg-rose-50 text-slate-400 hover:text-rose-600 flex items-center justify-center" data-testid={`remove-field-${f.key}`}><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
        {fields.length === 0 && <p className="text-sm text-slate-400 py-3 text-center">No fields — add some below.</p>}
      </div>

      <div className="flex flex-col sm:flex-row gap-2">
        <select className={inputCls} value="" onChange={(e) => onPickField(e.target.value)} data-testid="add-builtin-field">
          <option value="">+ Add a field…</option>
          {available.length > 0 && (
            <optgroup label="Standard Fields">
              {available.map((f) => <option key={f.key} value={f.key}>{f.label}</option>)}
            </optgroup>
          )}
          {libraryAvailable.length > 0 && (
            <optgroup label="Saved Custom Fields">
              {libraryAvailable.map((f) => <option key={f.key} value={`lib::${f.key}`}>{f.label}</option>)}
            </optgroup>
          )}
        </select>
        <button type="button" onClick={openCustom} className={`${btnSecondary} sm:w-auto whitespace-nowrap`} data-testid="add-custom-field-btn"><Plus className="w-4 h-4" /> Add Custom Field</button>
      </div>

      {/* Custom field create / edit */}
      <Dialog open={dlg} onOpenChange={setDlg}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editIdx !== null ? "Edit Field" : "Add Custom Field"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><label className={labelCls}>Field Type</label>
              <select className={inputCls} value={draft.type} onChange={(e) => setDraft({ ...draft, type: e.target.value })} data-testid="custom-field-type">
                {CUSTOM_FIELD_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
            <div><label className={labelCls}>Field Label</label><input className={inputCls} value={draft.label} onChange={(e) => setDraft({ ...draft, label: e.target.value })} placeholder="e.g. Division" data-testid="custom-field-label" /></div>
            {(draft.type === "text" || draft.type === "number" || draft.type === "tel") && (
              <div><label className={labelCls}>Placeholder</label><input className={inputCls} value={draft.placeholder} onChange={(e) => setDraft({ ...draft, placeholder: e.target.value })} placeholder="e.g. Enter roll number" /></div>
            )}
            {draft.type === "select" && <OptionsEditor options={draft.options} onChange={(options) => setDraft({ ...draft, options })} />}
            {draft.type === "checkbox" && (
              <div><label className={labelCls}>Checkbox Label</label><input className={inputCls} value={draft.checkbox_label} onChange={(e) => setDraft({ ...draft, checkbox_label: e.target.value })} placeholder="I agree to the terms and conditions" data-testid="custom-checkbox-label" /></div>
            )}
            <div><label className={labelCls}>Help Text (optional)</label><input className={inputCls} value={draft.help_text} onChange={(e) => setDraft({ ...draft, help_text: e.target.value })} /></div>
            <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer"><input type="checkbox" checked={draft.required} onChange={(e) => setDraft({ ...draft, required: e.target.checked })} className="rounded border-slate-300 text-blue-600" data-testid="custom-field-required" /> Required (show on customer form)</label>
            <FieldPreview f={draft} />
          </div>
          <DialogFooter>
            <button className={btnSecondary} onClick={() => setDlg(false)}>Cancel</button>
            <button className={btnPrimary} onClick={saveDraft} data-testid="save-custom-field-btn">{editIdx !== null ? "Save Field" : "Add Field"}</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Built-in select options editor */}
      <Dialog open={optsIdx !== null} onOpenChange={(o) => !o && setOptsIdx(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Configure {optsIdx !== null ? fields[optsIdx].label : ""} Options</DialogTitle></DialogHeader>
          {optsIdx !== null && <OptionsEditor options={fields[optsIdx].options || []} onChange={(options) => update(optsIdx, { options })} />}
          <DialogFooter><button className={btnPrimary} onClick={() => setOptsIdx(null)}>Done</button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function OptionsEditor({ options, onChange }) {
  const set = (i, v) => onChange(options.map((o, idx) => (idx === i ? v : o)));
  const add = () => onChange([...options, ""]);
  const del = (i) => onChange(options.filter((_, idx) => idx !== i));
  const move = (i, dir) => {
    const j = i + dir; if (j < 0 || j >= options.length) return;
    const c = [...options]; [c[i], c[j]] = [c[j], c[i]]; onChange(c);
  };
  return (
    <div>
      <label className={labelCls}>Dropdown Options</label>
      <div className="space-y-2">
        {options.map((o, i) => (
          <div key={i} className="flex items-center gap-2">
            <input className={inputCls} value={o} onChange={(e) => set(i, e.target.value)} placeholder={`Option ${i + 1}`} data-testid="option-input" />
            <button type="button" onClick={() => move(i, -1)} className="text-slate-400 hover:text-slate-700"><ChevronUp className="w-4 h-4" /></button>
            <button type="button" onClick={() => move(i, 1)} className="text-slate-400 hover:text-slate-700"><ChevronDown className="w-4 h-4" /></button>
            <button type="button" onClick={() => del(i)} className="text-slate-400 hover:text-rose-600"><X className="w-4 h-4" /></button>
          </div>
        ))}
      </div>
      <button type="button" onClick={add} className="mt-2 text-sm text-blue-600 font-medium flex items-center gap-1" data-testid="add-option-btn"><Plus className="w-4 h-4" /> Add Option</button>
    </div>
  );
}

function FieldPreview({ f }) {
  if (!f.label) return null;
  return (
    <div className="bg-slate-50 border border-slate-200 rounded-xl p-3">
      <div className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-2">Preview</div>
      {f.type === "checkbox" ? (
        <label className="flex items-center gap-2 text-sm text-slate-700"><input type="checkbox" disabled className="rounded border-slate-300" /> {f.checkbox_label || f.label}</label>
      ) : f.type === "select" ? (
        <div><div className="text-xs font-semibold text-slate-600 mb-1">{f.label}</div><select disabled className={inputCls}><option>Select {f.label}…</option>{f.options.filter(Boolean).map((o, i) => <option key={i}>{o}</option>)}</select></div>
      ) : (
        <div><div className="text-xs font-semibold text-slate-600 mb-1">{f.label}</div><input disabled className={inputCls} placeholder={f.placeholder || ""} /></div>
      )}
    </div>
  );
}
